import { useState, useEffect } from 'react'
import { AddButton } from 'components/common/AddButton'
import { CreateChecklistTemplateModal } from './CreateChecklistTemplateModal'
import { useLoading } from 'hooks'
import { makeGetRequest, makePostRequest, makeDeleteRequest, makePutRequest } from 'utils/api'
import { List } from './List'
import { useDispatch } from 'react-redux'
import { PUSH_NOTIFICATION } from 'actions/actionTypes'
import { withSweetAlert } from 'hocs'

const ChecklistTemplates = () => {
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false)
  const [templates, setTemplates] = useState([])
  const reduxDispatch = useDispatch()
  const [editedTemplate, setEditedTemplate] = useState(null)

  const [, withLoadingLocks] = useLoading()

  useEffect(() => {
    ;(async () => {
      const { checkLists } = await withLoadingLocks(makeGetRequest('/v1/checklist/template'))
      setTemplates(checkLists)
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const openEditModal = async template => {
    try {
      const response = await withLoadingLocks(
        makeGetRequest(`/v1/checklist/template/${template.id}`)
      )
      setEditedTemplate(response)
      setIsCreateModalVisible(true)
    } catch (e) {
      setIsCreateModalVisible(true)
      reduxDispatch({
        type: PUSH_NOTIFICATION,
        payload: {
          title: 'There was a problem fetching template',
          level: 'error'
        }
      })
    }
  }

  const deleteTemplate = id => {
    const sweetAlertFactory = withSweetAlert(
      <div>
        <p style={{ color: 'red' }}>This action can&#39;t be reverted</p>
      </div>
    )
    sweetAlertFactory(
      {
        title: 'Are you sure you want to delete this template?',
        buttons: ['No', 'Yes'],
        icon: 'warning'
      },
      async () => {
        try {
          await makeDeleteRequest(`/v1/checklist/template/${id}`)
          const array = [...templates]
          const index = templates.findIndex(u => u.id === id)
          if (~index) {
            array.splice(index, 1)
            setTemplates(array)
            reduxDispatch({
              type: PUSH_NOTIFICATION,
              payload: {
                title: 'Template deleted successfuly',
                level: 'success'
              }
            })
          }
        } catch (e) {
          reduxDispatch({
            type: PUSH_NOTIFICATION,
            payload: {
              title: 'There was a problem deleting template',
              level: 'error'
            }
          })
        }
      },
      { cancelConfirmation: false }
    )
  }

  const cloneTemplate = async id => {
    try {
      const template = await withLoadingLocks(makePostRequest(`/v1/checklist/template/${id}/clone`))
      setTemplates([...templates, template])

      reduxDispatch({
        type: PUSH_NOTIFICATION,
        payload: {
          title: 'Template cloned successfully',
          level: 'success'
        }
      })
    } catch (e) {
      reduxDispatch({
        type: PUSH_NOTIFICATION,
        payload: {
          title: 'There was a problem cloning template',
          level: 'error'
        }
      })
    }
  }

  const clearModal = () => {
    setIsCreateModalVisible(false)
    setEditedTemplate(null)
  }

  const saveTemplate = async template => {
    try {
      if (editedTemplate) {
        const response = await makePutRequest(
          `/v1/checklist/template/${editedTemplate.id}`,
          template
        )
        const array = [...templates]
        const index = templates.findIndex(u => u.id === editedTemplate.id)
        if (~index) {
          array.splice(index, 1, response)
          setTemplates(array)
        }
      } else {
        const response = await makePostRequest('/v1/checklist/template', template)
        setTemplates([...templates, response])
      }
      clearModal()
      reduxDispatch({
        type: PUSH_NOTIFICATION,
        payload: {
          title: `Checklist template ${editedTemplate ? 'updated' : 'created'} successfuly`,
          level: 'success',
          autoDismiss: 20
        }
      })
    } catch (error) {
      clearModal()
      reduxDispatch({
        type: PUSH_NOTIFICATION,
        payload: {
          title: `There was a problem ${editedTemplate ? 'updating' : 'creating'} template`,
          level: 'error'
        }
      })
    }
  }

  return (
    <div>
      <AddButton onClick={() => setIsCreateModalVisible(true)}>
        Add New Checklist Template
      </AddButton>
      {templates.length > 0 && (
        <List
          templates={templates}
          deleteTemplate={deleteTemplate}
          openEditModal={openEditModal}
          cloneTemplate={cloneTemplate}
        />
      )}
      {isCreateModalVisible && (
        <CreateChecklistTemplateModal
          editedTemplate={editedTemplate}
          isModalVisible={isCreateModalVisible}
          clearModal={clearModal}
          saveTemplate={saveTemplate}
          toggleModal={() => setIsCreateModalVisible(!isCreateModalVisible)}
        />
      )}
    </div>
  )
}

export default ChecklistTemplates
